<template>
  <div id="editor-wrapper">
    <div class="avatar" :class="{ hidden: !$root.userLoggedIn }">
      <div class="avatar-overlay" v-if="comment.length == 0" />
      <avatar :user="user" size="sm" />
    </div>
    <div class="vue-easymde" id="vue-easymde">
      <p id="heading">
        {{ heading }}
        <img :src="require('@img/icons-new-gray.svg')" />
      </p>
      <div id="add-display-name" v-if="nameRequired">
        <div id="display-name-form">
          <input
            type="text"
            ref="fullName"
            data-testid="comment-display-name"
            placeholder="Full name"
            @keyup.enter.exact="submitDisplayName"
          />
          <button id="submitDisplayName" href="#" @click="submitDisplayName()" data-testid="add-display-name">
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>
        <span class="display-name-error" v-if="anonymousNameError">Name must be at least 3 characters long</span>
      </div>
      <div id="submit-comment" v-else>
        <vue-tribute :options="tributeOptions">
          <ResizeAuto>
            <template v-slot:default="{ resize }">
              <textarea
                rows="1"
                id="new_comment"
                class="textarea vue-simplemde-textarea"
                :name="name"
                v-model="comment"
                :class="{ error }"
                @tribute-replaced="replacedName"
                :placeholder="isPersonal || !userLoggedIn ? 'Add to the conversation' : 'Type @ to mention a person'"
                @keydown.stop
                @keydown.enter.exact.prevent
                @keyup.enter.exact="onEnter"
                @input="(e) => onInput(e, resize)"
                ref="new_comment"
              ></textarea>
            </template>
          </ResizeAuto>
        </vue-tribute>

        <button
          id="submitComment"
          name="commit"
          value="Post"
          @click="checkAnonymous()"
          data-disable-with="Post"
          data-testid="post-comment"
          class="editor-submit submitComment"
        >
          <i class="fas fa-paper-plane" :class="{ active: comment.length > 0 }"></i>
        </button>
        <p class="error-message" v-if="error">{{ error }}</p>
      </div>
      <div v-if="isTyping" class="collaborator-tip">
        Pro tip &#11088; &nbsp;:
        <a :href="collaborationLink">
          {{ collaborationTip }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import VueTribute from "vue-tribute";
import ResizeAuto from "../resize_auto";
import Avatar from "@cmp/shared/avatar";
import { mapState } from "vuex";

import "tributejs/tribute.css";

export default {
  components: { VueTribute, ResizeAuto, Avatar },
  props: {
    name: String,
    commentableType: String,
    tributeOptions: Object,
  },
  data: function() {
    return {
      comment: "",
      heading: "Post new comment",
      mentions: [],
      disableEnter: false,
      submittingComment: false,
      displayName: localStorage.getItem("commentsDisplayName"),
      nameRequired: false,
      anonymousNameError: false,
      isTyping: false,
      error: null,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.user,
      isPersonal: (state) => state.currentOrganization.attributes.personal,
      commentableId: (state) => state.item.id,
    }),
    userLoggedIn: function() {
      return this.$root.userLoggedIn;
    },
    collaborationTip: function() {
      return this.isPersonal
        ? "Create a team to mention collabrators in comments!"
        : "Invite team members to mention them!";
    },
    collaborationLink: function() {
      return this.isPersonal
        ? "/purchase?period=annual&plan=team&trial=true&utm_source=viewer&utm_campaign=comments"
        : "#invite";
    },
  },
  updated() {
    if (this.nameRequired) {
      this.$refs.fullName.focus();
    }
  },
  methods: {
    onEnter: function(_event) {
      const vm = this;
      if (vm.disableEnter) {
        vm.disableEnter = false;
        return;
      }

      if (!vm.submittingComment) {
        vm.submittingComment = true;
        vm.checkAnonymous().finally(() => {
          vm.submittingComment = false;
        });
      }
    },
    replacedName: function(event) {
      this.disableEnter = true;
      this.mentions.push(event["detail"]["item"]["original"]);
    },
    submitDisplayName: function() {
      const name = this.$refs.fullName.value.trim();
      if (name.length > 3) {
        this.anonymousNameError = false;
        this.displayName = name;
        localStorage.setItem("commentsDisplayName", name);
        this.checkAnonymous();
      } else {
        this.anonymousNameError = true;
      }
    },
    checkAnonymous: async function() {
      if (this.comment.length === 0) {
        return;
      }

      if (!this.userLoggedIn && !this.displayName) {
        this.nameRequired = true;
        this.heading = "Please add your name to comment";
      } else {
        this.nameRequired = false;
        return this.createComment();
      }
    },
    createComment: function(_) {
      const vm = this;
      localStorage.setItem("open_same_tab", true);
      let content = vm.comment;
      vm.error = null;

      const params = {
        content: content.trim(),
        commentable_id: vm.commentableId,
        commentable_type: vm.commentableType,
        mentions: vm.mentions,
        display_name: vm.displayName,
      };

      return vm.$store
        .dispatch("createComment", params)
        .then((r) => {
          vm.heading = "Post new comment";
          vm.comment = "";
        })
        .catch(function(e) {
          vm.error = e.response.data.errors[0].detail;
        });
    },
    onInput: function(e, resizeHandler) {
      if (!this.isTyping) {
        this.isTyping = true;
      }

      if (resizeHandler) {
        resizeHandler(e);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "application.scss";

#editor-wrapper {
  display: flex;
}

.avatar {
  margin-top: 0.5rem;
  margin-right: 1rem;
  position: relative;

  &.hidden {
    visibility: hidden;
  }
}

.avatar-overlay {
  width: 3rem;
  height: 3rem;
  position: absolute;
  z-index: 1;
  background: rgba(231, 235, 240, 0.5);
  border-radius: 30px;
}

#heading {
  font-weight: bold;
  margin-bottom: 5px;
  color: $dark-gray;
}

#add-display-name {
  display: flex;
  flex-direction: column;

  #display-name-form {
    display: flex;
    justify-content: space-between;
    position: relative;

    input {
      flex-grow: 2;
      padding: 0.5rem;
      border-radius: 5px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
      border: solid 1px #b0b0b0;
      background-color: var(--c-white);

      &:focus {
        outline: 0 none;
        border: 1px solid #b0b0b0;
      }
    }

    button {
      background: transparent;
      position: absolute;
      border: none;
      margin-right: 5px;
      top: 9px;
      right: 4px;

      .fa-paper-plane {
        color: $bright-blue;
      }
    }
  }

  .display-name-error {
    font-size: 0.7rem;
    color: $radical-red;
  }
}

.vue-easymde {
  flex-grow: 2;
  background-color: #ffffff;
  padding: 1rem;
  border-radius: 4px;

  .new-comment-text {
    font-family: SFPro;
    font-size: 0.75rem;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
  }

  .new-comment {
    margin-top: 0.75rem;
    display: flex;
  }

  .vue-simplemde-textarea {
    overflow: auto;
    resize: none;
    width: 100%;
    border-radius: 5px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
    border: solid 1px #b0b0b0;
    background-color: var(--c-white);
    padding: 0.5rem 2rem 0.5rem 0.5rem;
    max-height: 10rem;
    font-size: 0.75rem;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: normal;

    &.error {
      border: 1px solid red;
    }
  }

  .vue-simplemde-textarea:focus {
    outline: 0 none;
    -webkit-appearance: none;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
    border: solid 1px #b0b0b0;

    &.error {
      border: 1px solid red;
    }
  }
}

.error-message {
  color: red;
  font-size: 0.7rem;
  margin-top: -4px;
}

#submit-comment {
  position: relative;
}

.editor-submit {
  border: none;
  position: absolute;
  position: absolute;
  top: 3px;
  right: 4px;
  background: transparent;

  .fa-paper-plane {
    color: $dark-gray;

    &.active {
      color: $bright-blue;
    }
  }
}
.editor-submit:focus {
  outline: none;
  box-shadow: none;
}
.collaborator-tip {
  color: $bright-blue;
}

@media (max-width: 767px) {
  .avatar,
  #heading {
    display: none;
  }

  .vue-easymde {
    position: fixed;
    width: 100%;
    // height of mobile nav bar to toggle overview/comments
    bottom: 43px;
    left: 0;
    padding: 0.5rem;
  }
}
</style>
